import { GameState } from '../game/GameState';
import type {
  CasusBelliId,
  DiplomaticAgreementState,
  DiplomaticAgreementType,
  DiplomaticRelationState,
  PeaceTermId,
  StrategicInterest,
  SubjectContractState,
  SubjectType,
} from '../types/game';
import { DataRegistry } from './DataRegistry';
import { TradeManager } from './TradeManager';
import { WarManager } from './WarManager';

const AGREEMENT_TREATY_KEYS: Partial<Record<DiplomaticAgreementType, string>> = {
  guarantee: 'guarantees',
  league: 'leagues',
  trade_concession: 'tradeConcessions',
  basing_rights: 'basingRights',
  dynastic_marriage: 'marriages',
};

const SUBJECT_DEFAULTS: Record<SubjectType, Pick<SubjectContractState, 'autonomy' | 'taxRate' | 'manpowerLevy' | 'tradeShare' | 'warObligation' | 'religiousProtection' | 'culturalAutonomy' | 'basingRights'>> = {
  tributary: { autonomy: 78, taxRate: 0.12, manpowerLevy: 0, tradeShare: 0.08, warObligation: 'none', religiousProtection: true, culturalAutonomy: true, basingRights: false },
  protectorate: { autonomy: 58, taxRate: 0.05, manpowerLevy: 0.04, tradeShare: 0.16, warObligation: 'defensive', religiousProtection: true, culturalAutonomy: true, basingRights: true },
  vassal: { autonomy: 34, taxRate: 0.2, manpowerLevy: 0.12, tradeShare: 0.12, warObligation: 'all', religiousProtection: false, culturalAutonomy: false, basingRights: true },
};

export class DiplomacyManager {
  static readonly instance = new DiplomacyManager();

  ensureCampaignInitialized() {
    const campaign = GameState.instance.requireCampaign();
    campaign.diplomaticRelations ??= {};
    campaign.diplomaticAgreements ??= {};
    campaign.subjectContracts ??= {};

    for (const faction of Object.values(campaign.factions)) {
      faction.treaties ??= {};
      for (const key of ['alliances', 'defensivePacts', 'nonAggression', 'trade', 'militaryAccess', 'marriages', 'embargoes', 'vassals', 'guarantees', 'leagues', 'tradeConcessions', 'basingRights', 'tributaries', 'protectorates', 'overlords']) faction.treaties[key] ??= [];
      faction.diplomaticReputation ??= 0;
    }

    const factions = Object.keys(campaign.factions);
    for (const sourceId of factions) {
      for (const targetId of factions) {
        if (sourceId === targetId) continue;
        const key = this.relationKey(sourceId, targetId);
        campaign.diplomaticRelations[key] ??= this.createInitialRelation(sourceId, targetId);
      }
    }

    this.migrateLegacySubjects();
    this.migrateLegacyAgreements();
    campaign.selectedDiplomaticFactionId ??= factions.find((id) => id !== campaign.playerFactionId);
  }

  processMonth() {
    this.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();

    for (const relation of Object.values(campaign.diplomaticRelations)) {
      const source = campaign.factions[relation.sourceFactionId];
      const target = campaign.factions[relation.targetFactionId];
      if (!source || !target) continue;
      relation.threat = Math.max(0, relation.threat - (source.atWarWith.includes(target.id) ? 0 : 0.7));
      relation.favors = Math.min(100, relation.favors + this.monthlyFavorGain(source.id, target.id));
      relation.trust = Math.max(-100, Math.min(100, relation.trust + this.monthlyTrustDrift(source.id, target.id)));
      relation.strategicInterest = this.calculateStrategicInterest(source.id, target.id);
      relation.diplomaticReputation = target.diplomaticReputation;
    }

    for (const agreement of Object.values(campaign.diplomaticAgreements)) {
      if (agreement.active && agreement.expiresTurn && agreement.expiresTurn <= campaign.turn) this.deactivateAgreement(agreement, 'expired');
    }

    for (const contract of Object.values(campaign.subjectContracts)) this.processSubject(contract);
    this.processAiDiplomacy();
  }

  relation(sourceFactionId: string, targetFactionId: string) {
    this.ensureCampaignInitialized();
    return GameState.instance.requireCampaign().diplomaticRelations[this.relationKey(sourceFactionId, targetFactionId)];
  }

  opinion(sourceFactionId: string, targetFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const sourceData = DataRegistry.instance.factions[sourceFactionId];
    const targetData = DataRegistry.instance.factions[targetFactionId];
    const source = campaign.factions[sourceFactionId];
    const target = campaign.factions[targetFactionId];
    const relation = this.relation(sourceFactionId, targetFactionId);
    if (!sourceData || !targetData || !source || !target || !relation) return 0;

    let score = relation.trust * 0.55 - relation.threat * 0.62 + relation.favors * 0.12 + target.diplomaticReputation * 0.22;
    if (source.stateReligion === target.stateReligion) score += 14;
    else score -= Math.max(0, 10 - source.tolerance * 0.08 - source.syncretism * 0.06);
    if (source.primaryCulture === target.primaryCulture) score += 8;
    else if (source.acceptedCultures.includes(target.primaryCulture)) score += 4;
    if (source.treaties.trade.includes(targetFactionId)) score += 12;
    if (source.treaties.alliances.includes(targetFactionId)) score += 26;
    if (source.treaties.marriages.includes(targetFactionId)) score += 14;
    if (source.treaties.nonAggression.includes(targetFactionId)) score += 8;
    if (source.treaties.guarantees.includes(targetFactionId)) score += 8;
    if (source.treaties.leagues.includes(targetFactionId)) score += 10;
    if (source.treaties.embargoes.includes(targetFactionId)) score -= 28;
    if (source.atWarWith.includes(targetFactionId)) score -= 85;
    if ((source.truceUntil[targetFactionId] ?? 0) > campaign.turn) score += 5;
    if (source.claims.some((provinceId) => campaign.provinces[provinceId]?.owner === targetFactionId)) score -= 16;
    if (this.subjectContractBetween(sourceFactionId, targetFactionId)) score += 5;
    score += sourceData.diplomaticBiases?.[targetFactionId] ?? 0;
    score += this.interestModifier(relation.strategicInterest);
    return Math.round(Math.max(-100, Math.min(100, score)));
  }

  relationBreakdown(sourceFactionId: string, targetFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const source = campaign.factions[sourceFactionId];
    const target = campaign.factions[targetFactionId];
    const relation = this.relation(sourceFactionId, targetFactionId);
    if (!source || !target || !relation) return [];
    const entries: Array<[string, number]> = [
      ['Trust', Math.round(relation.trust * 0.55)],
      ['Threat', Math.round(-relation.threat * 0.62)],
      ['Favors', Math.round(relation.favors * 0.12)],
      ['Reputation', Math.round(target.diplomaticReputation * 0.22)],
      ['Strategic interest', this.interestModifier(relation.strategicInterest)],
    ];
    if (source.stateReligion === target.stateReligion) entries.push(['Shared faith', 14]);
    else entries.push(['Religious difference', -Math.round(Math.max(0, 10 - source.tolerance * 0.08 - source.syncretism * 0.06))]);
    if (source.primaryCulture === target.primaryCulture) entries.push(['Shared culture', 8]);
    if (source.treaties.alliances.includes(targetFactionId)) entries.push(['Alliance', 26]);
    if (source.treaties.trade.includes(targetFactionId)) entries.push(['Trade ties', 12]);
    if (source.treaties.marriages.includes(targetFactionId)) entries.push(['Dynastic marriage', 14]);
    if (source.claims.some((provinceId) => campaign.provinces[provinceId]?.owner === targetFactionId)) entries.push(['Territorial claims', -16]);
    if (source.atWarWith.includes(targetFactionId)) entries.push(['At war', -85]);
    return entries;
  }

  agreementsBetween(firstFactionId: string, secondFactionId: string) {
    return Object.values(GameState.instance.requireCampaign().diplomaticAgreements)
      .filter((agreement) => agreement.active && agreement.memberFactionIds.includes(firstFactionId) && agreement.memberFactionIds.includes(secondFactionId))
      .sort((a, b) => a.type.localeCompare(b.type));
  }

  subjectsOf(overlordFactionId: string) {
    return Object.values(GameState.instance.requireCampaign().subjectContracts)
      .filter((contract) => contract.overlordFactionId === overlordFactionId)
      .sort((a, b) => b.libertyDesire - a.libertyDesire);
  }

  overlordOf(subjectFactionId: string) {
    return Object.values(GameState.instance.requireCampaign().subjectContracts).find((contract) => contract.subjectFactionId === subjectFactionId);
  }

  subjectContractBetween(firstFactionId: string, secondFactionId: string) {
    return Object.values(GameState.instance.requireCampaign().subjectContracts)
      .find((contract) => (contract.overlordFactionId === firstFactionId && contract.subjectFactionId === secondFactionId) || (contract.overlordFactionId === secondFactionId && contract.subjectFactionId === firstFactionId));
  }

  declareWar(sourceFactionId: string, targetFactionId: string, casusBelli: CasusBelliId = 'conquest', warGoalProvinceId?: string) {
    const sourceContract = this.overlordOf(sourceFactionId);
    if (sourceContract && sourceContract.libertyDesire < 70) {
      GameState.instance.log('A loyal subject cannot declare an independent war.', 'warning');
      return false;
    }
    const declared = WarManager.instance.declareWar(sourceFactionId, targetFactionId, casusBelli, warGoalProvinceId);
    if (!declared) return false;
    this.adjustThreat(sourceFactionId, targetFactionId, 24);
    this.adjustTrust(targetFactionId, sourceFactionId, -35);
    this.attachSubjectsToWar(sourceFactionId, targetFactionId);
    this.attachGuaranteesAndLeagues(sourceFactionId, targetFactionId);
    return true;
  }

  makePeace(sourceFactionId: string, targetFactionId: string, term: PeaceTermId) {
    const result = WarManager.instance.makePeace(sourceFactionId, targetFactionId, term);
    if (result && term === 'vassalize') this.createSubjectContract(sourceFactionId, targetFactionId, 'vassal', true);
    if (result) {
      this.adjustThreat(sourceFactionId, targetFactionId, term === 'white_peace' ? -8 : 4);
      this.adjustTrust(targetFactionId, sourceFactionId, term === 'white_peace' ? 3 : -12);
    }
    return result;
  }

  improveRelations(sourceFactionId: string, targetFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const source = campaign.factions[sourceFactionId];
    if (!source || source.treasury < 18 || source.atWarWith.includes(targetFactionId)) return false;
    source.treasury -= 18;
    this.adjustTrust(sourceFactionId, targetFactionId, 6);
    this.adjustTrust(targetFactionId, sourceFactionId, 4);
    const relation = this.relation(sourceFactionId, targetFactionId);
    relation.favors = Math.min(100, relation.favors + 2);
    relation.lastActionTurn = campaign.turn;
    GameState.instance.log(`Envoys improved relations with ${DataRegistry.instance.factions[targetFactionId]?.name ?? targetFactionId}.`, 'success');
    return true;
  }

  sendGift(sourceFactionId: string, targetFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const source = campaign.factions[sourceFactionId];
    if (!source || source.treasury < 35) {
      GameState.instance.log('A diplomatic gift requires 35 gold.', 'warning');
      return false;
    }
    source.treasury -= 35;
    this.adjustTrust(targetFactionId, sourceFactionId, 12);
    this.adjustTrust(sourceFactionId, targetFactionId, 4);
    const relation = this.relation(sourceFactionId, targetFactionId);
    relation.favors = Math.min(100, relation.favors + 5);
    relation.lastActionTurn = campaign.turn;
    GameState.instance.log(`Gift sent to ${DataRegistry.instance.factions[targetFactionId]?.name ?? targetFactionId}.`, 'success');
    return true;
  }

  signTradeAgreement(sourceFactionId: string, targetFactionId: string) {
    if (WarManager.instance.warBetween(sourceFactionId, targetFactionId)) {
      GameState.instance.log('Trade agreements cannot be signed during war.', 'warning');
      return false;
    }
    const accepted = this.acceptance(sourceFactionId, targetFactionId, 5, 12);
    if (accepted < 0) {
      GameState.instance.log('The target considers a trade pact contrary to its interests.', 'warning');
      return false;
    }
    const result = TradeManager.instance.signTradeAgreement(sourceFactionId, targetFactionId);
    if (result) {
      this.adjustTrust(sourceFactionId, targetFactionId, 4);
      this.adjustTrust(targetFactionId, sourceFactionId, 4);
    }
    return result;
  }

  proposeAlliance(sourceFactionId: string, targetFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    if (WarManager.instance.warBetween(sourceFactionId, targetFactionId) || this.overlordOf(sourceFactionId) || this.overlordOf(targetFactionId)) return false;
    const relation = this.relation(sourceFactionId, targetFactionId);
    const requiredFavors = 12;
    if (this.acceptance(sourceFactionId, targetFactionId, 34, requiredFavors) < 0 || relation.favors < requiredFavors) {
      GameState.instance.log('Trust, favors or strategic alignment are too low for an alliance.', 'warning');
      return false;
    }
    const source = campaign.factions[sourceFactionId];
    const target = campaign.factions[targetFactionId];
    if (!source || !target) return false;
    this.addMutualTreaty('alliances', sourceFactionId, targetFactionId);
    relation.favors = Math.max(0, relation.favors - requiredFavors);
    this.adjustTrust(sourceFactionId, targetFactionId, 10);
    this.adjustTrust(targetFactionId, sourceFactionId, 10);
    GameState.instance.log(`Alliance formed with ${DataRegistry.instance.factions[targetFactionId]?.name ?? targetFactionId}.`, 'success');
    return true;
  }

  signNonAggressionPact(sourceFactionId: string, targetFactionId: string) {
    if (WarManager.instance.warBetween(sourceFactionId, targetFactionId) || this.acceptance(sourceFactionId, targetFactionId, -5, 3) < 0) return false;
    this.addMutualTreaty('nonAggression', sourceFactionId, targetFactionId);
    this.adjustTrust(sourceFactionId, targetFactionId, 5);
    this.adjustTrust(targetFactionId, sourceFactionId, 5);
    return true;
  }

  grantMilitaryAccess(sourceFactionId: string, targetFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    if (WarManager.instance.warBetween(sourceFactionId, targetFactionId) || this.opinion(targetFactionId, sourceFactionId) < 5) {
      GameState.instance.log('Opinion is too low for military access.', 'warning');
      return false;
    }
    if (!campaign.factions[targetFactionId].treaties.militaryAccess.includes(sourceFactionId)) campaign.factions[targetFactionId].treaties.militaryAccess.push(sourceFactionId);
    GameState.instance.log(`Military access arranged with ${DataRegistry.instance.factions[targetFactionId]?.name ?? targetFactionId}.`, 'success');
    return true;
  }

  createAgreement(sourceFactionId: string, targetFactionId: string, type: DiplomaticAgreementType) {
    const campaign = GameState.instance.requireCampaign();
    if (sourceFactionId === targetFactionId || WarManager.instance.warBetween(sourceFactionId, targetFactionId)) return false;
    if (this.agreementsBetween(sourceFactionId, targetFactionId).some((agreement) => agreement.type === type)) return false;

    const requirements: Record<DiplomaticAgreementType, { opinion: number; favors: number; cost: number }> = {
      guarantee: { opinion: -15, favors: 0, cost: 20 },
      league: { opinion: 22, favors: 8, cost: 35 },
      trade_concession: { opinion: 8, favors: 4, cost: 25 },
      basing_rights: { opinion: 16, favors: 6, cost: 30 },
      dynastic_marriage: { opinion: 24, favors: 8, cost: 40 },
    };
    const requirement = requirements[type];
    const source = campaign.factions[sourceFactionId];
    const relation = this.relation(sourceFactionId, targetFactionId);
    if (!source || source.treasury < requirement.cost || relation.favors < requirement.favors || this.acceptance(sourceFactionId, targetFactionId, requirement.opinion, requirement.favors) < 0) {
      GameState.instance.log(`The proposed ${type.replaceAll('_', ' ')} lacks money, favors or diplomatic support.`, 'warning');
      return false;
    }

    source.treasury -= requirement.cost;
    relation.favors -= requirement.favors;
    const id = `agreement_${type}_${sourceFactionId}_${targetFactionId}_${campaign.turn}`;
    const agreement: DiplomaticAgreementState = {
      id,
      type,
      memberFactionIds: [sourceFactionId, targetFactionId],
      createdTurn: campaign.turn,
      expiresTurn: type === 'guarantee' ? undefined : campaign.turn + (type === 'dynastic_marriage' ? 180 : 72),
      active: true,
      terms: type === 'trade_concession' ? { tradeIncomeBonus: 0.12, tariffReduction: 0.08 }
        : type === 'basing_rights' ? { navalRepair: true, supplyAccess: true }
          : type === 'league' ? { defensiveIntervention: true, favorGain: 0.25 }
            : type === 'guarantee' ? { defensiveIntervention: true }
              : { legitimacyBonus: 3, trustGain: 8 },
    };
    campaign.diplomaticAgreements[id] = agreement;
    this.syncAgreementTreaty(agreement, true);
    this.adjustTrust(sourceFactionId, targetFactionId, type === 'dynastic_marriage' ? 12 : 6);
    this.adjustTrust(targetFactionId, sourceFactionId, type === 'dynastic_marriage' ? 12 : 6);
    GameState.instance.log(`${type.replaceAll('_', ' ')} concluded with ${DataRegistry.instance.factions[targetFactionId]?.name ?? targetFactionId}.`, 'success');
    return true;
  }

  breakAgreement(sourceFactionId: string, agreementId: string) {
    const agreement = GameState.instance.requireCampaign().diplomaticAgreements[agreementId];
    if (!agreement?.active || !agreement.memberFactionIds.includes(sourceFactionId)) return false;
    this.deactivateAgreement(agreement, 'broken');
    for (const targetId of agreement.memberFactionIds.filter((id) => id !== sourceFactionId)) {
      this.adjustTrust(targetId, sourceFactionId, -18);
      this.adjustThreat(targetId, sourceFactionId, 8);
    }
    GameState.instance.requireCampaign().factions[sourceFactionId].diplomaticReputation = Math.max(-100, GameState.instance.requireCampaign().factions[sourceFactionId].diplomaticReputation - 8);
    return true;
  }

  createSubjectContract(overlordFactionId: string, subjectFactionId: string, type: SubjectType, imposedByPeace = false) {
    const campaign = GameState.instance.requireCampaign();
    const overlord = campaign.factions[overlordFactionId];
    const subject = campaign.factions[subjectFactionId];
    if (!overlord || !subject || overlordFactionId === subjectFactionId || this.overlordOf(subjectFactionId)) return false;
    if (!imposedByPeace) {
      const powerRatio = this.factionPower(overlordFactionId) / Math.max(1, this.factionPower(subjectFactionId));
      const opinion = this.opinion(subjectFactionId, overlordFactionId);
      const requiredRatio = type === 'vassal' ? 2.2 : type === 'protectorate' ? 1.45 : 1.05;
      if (powerRatio < requiredRatio || opinion < (type === 'vassal' ? 10 : -10)) {
        GameState.instance.log('The target is not willing or weak enough to accept that subject relationship.', 'warning');
        return false;
      }
    }

    const defaults = SUBJECT_DEFAULTS[type];
    const id = `subject_${overlordFactionId}_${subjectFactionId}`;
    campaign.subjectContracts[id] = {
      id,
      overlordFactionId,
      subjectFactionId,
      type,
      startedTurn: campaign.turn,
      libertyDesire: Math.max(12, Math.min(85, 55 - (this.factionPower(overlordFactionId) / Math.max(1, this.factionPower(subjectFactionId))) * 12 + (type === 'vassal' ? 14 : 0))),
      ...defaults,
      lastRenegotiatedTurn: campaign.turn,
    };
    this.syncSubjectTreaties(campaign.subjectContracts[id]);
    this.removeMutualTreaty('alliances', overlordFactionId, subjectFactionId);
    this.adjustTrust(subjectFactionId, overlordFactionId, type === 'tributary' ? 2 : -8);
    GameState.instance.log(`${DataRegistry.instance.factions[subjectFactionId]?.name ?? subjectFactionId} became a ${type} of ${DataRegistry.instance.factions[overlordFactionId]?.name ?? overlordFactionId}.`, 'success');
    return true;
  }

  renegotiateSubject(overlordFactionId: string, contractId: string, direction: 'loosen' | 'tighten') {
    const campaign = GameState.instance.requireCampaign();
    const contract = campaign.subjectContracts[contractId];
    const overlord = campaign.factions[overlordFactionId];
    if (!contract || contract.overlordFactionId !== overlordFactionId || !overlord || campaign.turn - contract.lastRenegotiatedTurn < 12) return false;
    const cost = direction === 'loosen' ? 30 : 55;
    if (overlord.treasury < cost) return false;
    overlord.treasury -= cost;
    contract.lastRenegotiatedTurn = campaign.turn;
    if (direction === 'loosen') {
      contract.autonomy = Math.min(95, contract.autonomy + 12);
      contract.taxRate = Math.max(0, contract.taxRate - 0.04);
      contract.manpowerLevy = Math.max(0, contract.manpowerLevy - 0.03);
      contract.libertyDesire = Math.max(0, contract.libertyDesire - 16);
      this.adjustTrust(contract.subjectFactionId, overlordFactionId, 8);
    } else {
      contract.autonomy = Math.max(10, contract.autonomy - 10);
      contract.taxRate = Math.min(0.35, contract.taxRate + 0.04);
      contract.manpowerLevy = Math.min(0.25, contract.manpowerLevy + 0.03);
      contract.libertyDesire = Math.min(100, contract.libertyDesire + 18);
      this.adjustTrust(contract.subjectFactionId, overlordFactionId, -10);
    }
    return true;
  }

  releaseSubject(overlordFactionId: string, contractId: string) {
    const campaign = GameState.instance.requireCampaign();
    const contract = campaign.subjectContracts[contractId];
    if (!contract || contract.overlordFactionId !== overlordFactionId) return false;
    delete campaign.subjectContracts[contractId];
    this.removeSubjectTreaties(contract);
    this.adjustTrust(contract.subjectFactionId, overlordFactionId, 20);
    campaign.factions[overlordFactionId].diplomaticReputation = Math.min(100, campaign.factions[overlordFactionId].diplomaticReputation + 5);
    return true;
  }

  callFavor(sourceFactionId: string, targetFactionId: string, favorCost = 10) {
    const relation = this.relation(sourceFactionId, targetFactionId);
    if (!relation || relation.favors < favorCost) return false;
    relation.favors -= favorCost;
    this.adjustTrust(targetFactionId, sourceFactionId, 3);
    return true;
  }

  acceptance(sourceFactionId: string, targetFactionId: string, requiredOpinion = 0, favorCost = 0) {
    const relation = this.relation(sourceFactionId, targetFactionId);
    const targetView = this.opinion(targetFactionId, sourceFactionId);
    const sourcePower = this.factionPower(sourceFactionId);
    const targetPower = this.factionPower(targetFactionId);
    const powerReason = Math.round((sourcePower - targetPower) / Math.max(50, targetPower) * 12);
    return targetView - requiredOpinion + Math.round(relation.favors - favorCost) + powerReason + GameState.instance.requireCampaign().factions[sourceFactionId].diplomaticReputation * 0.25;
  }

  factionPower(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    if (!faction) return 0;
    const provinces = Object.values(campaign.provinces).filter((province) => province.owner === factionId);
    const armies = Object.values(campaign.armies).filter((army) => army.factionId === factionId);
    const fleets = Object.values(campaign.fleets).filter((fleet) => fleet.factionId === factionId);
    return provinces.reduce((sum, province) => sum + province.population + province.prosperity * 0.7, 0)
      + armies.reduce((sum, army) => sum + army.units.reduce((total, unit) => total + unit.strength, 0) / 18, 0)
      + fleets.reduce((sum, fleet) => sum + fleet.ships.length * 22, 0)
      + faction.treasury * 0.18 + faction.prestige * 2;
  }

  private createInitialRelation(sourceFactionId: string, targetFactionId: string): DiplomaticRelationState {
    const campaign = GameState.instance.requireCampaign();
    const source = campaign.factions[sourceFactionId];
    const target = campaign.factions[targetFactionId];
    const sourceData = DataRegistry.instance.factions[sourceFactionId];
    const targetData = DataRegistry.instance.factions[targetFactionId];
    let trust = sourceData?.diplomaticBiases?.[targetFactionId] ?? 0;
    if (sourceData?.stateReligion === targetData?.stateReligion) trust += 8;
    if (sourceData?.primaryCulture === targetData?.primaryCulture) trust += 5;
    if (source?.treaties.alliances.includes(targetFactionId)) trust += 35;
    if (source?.treaties.trade.includes(targetFactionId)) trust += 10;
    if (source?.atWarWith.includes(targetFactionId)) trust -= 70;
    return {
      id: this.relationKey(sourceFactionId, targetFactionId),
      sourceFactionId,
      targetFactionId,
      trust: Math.max(-100, Math.min(100, trust)),
      favors: source?.treaties.alliances.includes(targetFactionId) ? 15 : source?.treaties.trade.includes(targetFactionId) ? 5 : 0,
      threat: source?.atWarWith.includes(targetFactionId) ? 50 : 0,
      strategicInterest: this.calculateStrategicInterest(sourceFactionId, targetFactionId),
      diplomaticReputation: target?.diplomaticReputation ?? 0,
      lastActionTurn: campaign.turn,
      modifiers: {},
    };
  }

  private processSubject(contract: SubjectContractState) {
    const campaign = GameState.instance.requireCampaign();
    const overlord = campaign.factions[contract.overlordFactionId];
    const subject = campaign.factions[contract.subjectFactionId];
    if (!overlord || !subject) return;
    const subjectIncomeBase = Math.max(0, subject.treasury * 0.015 + Object.values(campaign.provinces).filter((province) => province.owner === subject.id).reduce((sum, province) => sum + province.prosperity * 0.025, 0));
    const tribute = Math.min(subject.treasury, Math.max(0, Math.round(subjectIncomeBase * contract.taxRate * 3)));
    if (tribute > 0) {
      subject.treasury -= tribute;
      overlord.treasury += tribute;
    }
    const subjectTradeValue = TradeManager.instance.routeSummaries().filter((route) => route.owner === subject.id).reduce((sum, route) => sum + route.value, 0);
    const tradeShareTransfer = Math.min(subject.treasury, Math.max(0, Math.round(subjectTradeValue * contract.tradeShare)));
    if (tradeShareTransfer > 0) {
      subject.treasury -= tradeShareTransfer;
      overlord.treasury += tradeShareTransfer;
    }
    const levy = Math.min(subject.manpower, Math.round(subject.maxManpower * contract.manpowerLevy / 24));
    if (levy > 0) {
      subject.manpower -= levy;
      overlord.manpower = Math.min(overlord.maxManpower, overlord.manpower + levy);
    }
    const powerRatio = this.factionPower(subject.id) / Math.max(1, this.factionPower(overlord.id));
    const trust = this.relation(subject.id, overlord.id)?.trust ?? 0;
    const identityPressure = (!contract.religiousProtection && subject.stateReligion !== overlord.stateReligion ? 8 : 0) + (!contract.culturalAutonomy && subject.primaryCulture !== overlord.primaryCulture ? 8 : 0);
    const pressure = contract.taxRate * 55 + contract.manpowerLevy * 70 + contract.tradeShare * 45 + (100 - contract.autonomy) * 0.18 + identityPressure + powerRatio * 18 - trust * 0.12 - overlord.legitimacy * 0.05;
    contract.libertyDesire = Math.max(0, Math.min(100, contract.libertyDesire * 0.93 + pressure * 0.07));
    if (contract.libertyDesire >= 90 && !subject.atWarWith.includes(overlord.id) && campaign.turn - contract.lastRenegotiatedTurn > 18) {
      const declared = WarManager.instance.declareWar(subject.id, overlord.id, 'defensive');
      if (declared) {
        GameState.instance.log(`${DataRegistry.instance.factions[subject.id]?.name ?? subject.id} launched a war of independence.`, 'danger');
        contract.lastRenegotiatedTurn = campaign.turn;
      }
    }
  }

  private processAiDiplomacy() {
    const campaign = GameState.instance.requireCampaign();
    if (campaign.turn % 3 !== 0) return;
    for (const source of Object.values(campaign.factions)) {
      if (source.id === campaign.playerFactionId || source.atWarWith.length) continue;
      const targets = Object.values(campaign.factions)
        .filter((target) => target.id !== source.id && !source.atWarWith.includes(target.id))
        .sort((a, b) => this.opinion(source.id, b.id) - this.opinion(source.id, a.id));
      const best = targets[0];
      if (!best) continue;
      const seed = this.hash(`${campaign.turn}:${source.id}:diplomacy`);
      if (source.treasury > 90 && this.opinion(source.id, best.id) > 28 && !source.treaties.trade.includes(best.id)) this.signTradeAgreement(source.id, best.id);
      else if (source.treasury > 120 && this.opinion(source.id, best.id) > 50 && !source.treaties.alliances.includes(best.id) && seed % 3 === 0) this.proposeAlliance(source.id, best.id);
      else if (source.treasury > 160 && seed % 5 === 0) this.sendGift(source.id, best.id);

      const subjectCandidates = targets.filter((target) => !this.overlordOf(target.id) && this.factionPower(source.id) > this.factionPower(target.id) * 1.6 && this.opinion(target.id, source.id) > -5);
      const candidate = subjectCandidates[0];
      if (candidate && seed % 7 === 0) this.createSubjectContract(source.id, candidate.id, this.factionPower(source.id) > this.factionPower(candidate.id) * 2.5 ? 'vassal' : 'protectorate');
    }
  }

  private attachSubjectsToWar(sourceFactionId: string, targetFactionId: string) {
    const war = WarManager.instance.warBetween(sourceFactionId, targetFactionId);
    if (!war) return;
    for (const contract of this.subjectsOf(sourceFactionId)) if (contract.warObligation === 'all') this.addParticipant(war.id, contract.subjectFactionId, 'attacker');
    for (const contract of this.subjectsOf(targetFactionId)) if (contract.warObligation !== 'none') this.addParticipant(war.id, contract.subjectFactionId, 'defender');
  }

  private attachGuaranteesAndLeagues(sourceFactionId: string, targetFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const war = WarManager.instance.warBetween(sourceFactionId, targetFactionId);
    if (!war) return;
    for (const agreement of Object.values(campaign.diplomaticAgreements)) {
      if (!agreement.active || !['guarantee', 'league'].includes(agreement.type) || !agreement.memberFactionIds.includes(targetFactionId)) continue;
      for (const memberId of agreement.memberFactionIds.filter((id) => id !== targetFactionId && id !== sourceFactionId)) this.addParticipant(war.id, memberId, 'defender');
    }
  }

  private addParticipant(warId: string, factionId: string, side: 'attacker' | 'defender') {
    const campaign = GameState.instance.requireCampaign();
    const war = campaign.wars[warId];
    const faction = campaign.factions[factionId];
    if (!war || !faction) return;
    const ownSide = side === 'attacker' ? war.attackers : war.defenders;
    const enemySide = side === 'attacker' ? war.defenders : war.attackers;
    if (!ownSide.includes(factionId)) ownSide.push(factionId);
    war.participation[factionId] ??= { battles: 0, casualties: 0, occupations: 0, contribution: 0 };
    for (const enemyId of enemySide) {
      const enemy = campaign.factions[enemyId];
      if (!enemy) continue;
      if (!faction.atWarWith.includes(enemyId)) faction.atWarWith.push(enemyId);
      if (!enemy.atWarWith.includes(factionId)) enemy.atWarWith.push(factionId);
    }
  }

  private calculateStrategicInterest(sourceFactionId: string, targetFactionId: string): StrategicInterest {
    const campaign = GameState.instance.requireCampaign();
    const source = campaign.factions[sourceFactionId];
    const target = campaign.factions[targetFactionId];
    if (!source || !target) return 'neutral';
    if (source.atWarWith.includes(targetFactionId)) return 'hostile';
    if (source.treaties.alliances.includes(targetFactionId)) return 'ally';
    const relation = campaign.diplomaticRelations?.[this.relationKey(sourceFactionId, targetFactionId)];
    const borders = this.sharedBorderCount(sourceFactionId, targetFactionId);
    const claims = source.claims.filter((provinceId) => campaign.provinces[provinceId]?.owner === targetFactionId).length;
    const powerRatio = this.factionPower(targetFactionId) / Math.max(1, this.factionPower(sourceFactionId));
    if (claims > 0 || (borders > 0 && (relation?.threat ?? 0) > 35)) return 'rival';
    if (powerRatio > 1.8 && borders > 0) return 'cautious';
    if ((relation?.trust ?? 0) > 42 || source.treaties.trade.includes(targetFactionId)) return 'friendly';
    return 'neutral';
  }

  private sharedBorderCount(firstFactionId: string, secondFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    let count = 0;
    for (const province of Object.values(campaign.provinces).filter((entry) => entry.owner === firstFactionId)) {
      const data = DataRegistry.instance.provinces[province.id];
      if (data?.neighbors.some((neighborId) => campaign.provinces[neighborId]?.owner === secondFactionId)) count++;
    }
    return count;
  }

  private monthlyFavorGain(sourceFactionId: string, targetFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const source = campaign.factions[sourceFactionId];
    if (!source) return 0;
    let gain = 0;
    if (source.treaties.alliances.includes(targetFactionId)) gain += 0.32;
    if (source.treaties.trade.includes(targetFactionId)) gain += 0.14;
    if (source.treaties.marriages.includes(targetFactionId)) gain += 0.12;
    if (source.treaties.leagues.includes(targetFactionId)) gain += 0.18;
    return gain;
  }

  private monthlyTrustDrift(sourceFactionId: string, targetFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const source = campaign.factions[sourceFactionId];
    const relation = campaign.diplomaticRelations[this.relationKey(sourceFactionId, targetFactionId)];
    if (!source || !relation) return 0;
    let drift = relation.trust > 0 ? -0.04 : relation.trust < 0 ? 0.05 : 0;
    if (source.treaties.alliances.includes(targetFactionId)) drift += 0.16;
    if (source.treaties.trade.includes(targetFactionId)) drift += 0.09;
    if (source.atWarWith.includes(targetFactionId)) drift -= 0.5;
    return drift;
  }

  private migrateLegacySubjects() {
    const campaign = GameState.instance.requireCampaign();
    for (const overlord of Object.values(campaign.factions)) {
      for (const subjectId of overlord.treaties.vassals ?? []) {
        if (!campaign.factions[subjectId] || this.overlordOfWithoutEnsure(subjectId)) continue;
        const defaults = SUBJECT_DEFAULTS.vassal;
        const id = `subject_${overlord.id}_${subjectId}`;
        campaign.subjectContracts[id] = { id, overlordFactionId: overlord.id, subjectFactionId: subjectId, type: 'vassal', startedTurn: campaign.turn, libertyDesire: 42, ...defaults, lastRenegotiatedTurn: campaign.turn };
        this.syncSubjectTreaties(campaign.subjectContracts[id]);
      }
    }
  }

  private migrateLegacyAgreements() {
    const campaign = GameState.instance.requireCampaign();
    for (const faction of Object.values(campaign.factions)) {
      for (const targetId of faction.treaties.marriages ?? []) {
        const pair = [faction.id, targetId].sort();
        const id = `agreement_dynastic_marriage_${pair[0]}_${pair[1]}_legacy`;
        campaign.diplomaticAgreements[id] ??= { id, type: 'dynastic_marriage', memberFactionIds: pair, createdTurn: Math.max(1, campaign.turn - 12), active: true, terms: { legitimacyBonus: 3, trustGain: 8 } };
      }
    }
  }

  private syncAgreementTreaty(agreement: DiplomaticAgreementState, active: boolean) {
    const key = AGREEMENT_TREATY_KEYS[agreement.type];
    if (!key) return;
    const campaign = GameState.instance.requireCampaign();
    for (const factionId of agreement.memberFactionIds) {
      const faction = campaign.factions[factionId];
      if (!faction) continue;
      for (const otherId of agreement.memberFactionIds.filter((id) => id !== factionId)) {
        if (active && !faction.treaties[key].includes(otherId)) faction.treaties[key].push(otherId);
        if (!active) faction.treaties[key] = faction.treaties[key].filter((id) => id !== otherId);
        if (agreement.type === 'basing_rights' && active && !faction.treaties.militaryAccess.includes(otherId)) faction.treaties.militaryAccess.push(otherId);
        if (agreement.type === 'basing_rights' && !active) faction.treaties.militaryAccess = faction.treaties.militaryAccess.filter((id) => id !== otherId);
      }
    }
  }

  private deactivateAgreement(agreement: DiplomaticAgreementState, reason: string) {
    agreement.active = false;
    agreement.terms.endedReason = reason;
    this.syncAgreementTreaty(agreement, false);
  }

  private syncSubjectTreaties(contract: SubjectContractState) {
    const campaign = GameState.instance.requireCampaign();
    const overlord = campaign.factions[contract.overlordFactionId];
    const subject = campaign.factions[contract.subjectFactionId];
    if (!overlord || !subject) return;
    if (!overlord.treaties.vassals.includes(subject.id)) overlord.treaties.vassals.push(subject.id);
    if (!subject.treaties.overlords.includes(overlord.id)) subject.treaties.overlords.push(overlord.id);
    const typeKey = contract.type === 'tributary' ? 'tributaries' : contract.type === 'protectorate' ? 'protectorates' : 'vassals';
    if (!overlord.treaties[typeKey].includes(subject.id)) overlord.treaties[typeKey].push(subject.id);
    if (contract.basingRights && !subject.treaties.basingRights.includes(overlord.id)) subject.treaties.basingRights.push(overlord.id);
    if (contract.basingRights && !subject.treaties.militaryAccess.includes(overlord.id)) subject.treaties.militaryAccess.push(overlord.id);
  }

  private removeSubjectTreaties(contract: SubjectContractState) {
    const campaign = GameState.instance.requireCampaign();
    const overlord = campaign.factions[contract.overlordFactionId];
    const subject = campaign.factions[contract.subjectFactionId];
    if (!overlord || !subject) return;
    for (const key of ['vassals', 'tributaries', 'protectorates']) overlord.treaties[key] = overlord.treaties[key].filter((id) => id !== subject.id);
    subject.treaties.overlords = subject.treaties.overlords.filter((id) => id !== overlord.id);
    subject.treaties.basingRights = subject.treaties.basingRights.filter((id) => id !== overlord.id);
    subject.treaties.militaryAccess = subject.treaties.militaryAccess.filter((id) => id !== overlord.id);
  }

  private addMutualTreaty(key: string, firstFactionId: string, secondFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const first = campaign.factions[firstFactionId];
    const second = campaign.factions[secondFactionId];
    if (!first || !second) return;
    if (!first.treaties[key].includes(secondFactionId)) first.treaties[key].push(secondFactionId);
    if (!second.treaties[key].includes(firstFactionId)) second.treaties[key].push(firstFactionId);
  }

  private removeMutualTreaty(key: string, firstFactionId: string, secondFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const first = campaign.factions[firstFactionId];
    const second = campaign.factions[secondFactionId];
    if (first) first.treaties[key] = first.treaties[key].filter((id) => id !== secondFactionId);
    if (second) second.treaties[key] = second.treaties[key].filter((id) => id !== firstFactionId);
  }

  private adjustTrust(sourceFactionId: string, targetFactionId: string, amount: number) {
    const relation = this.relation(sourceFactionId, targetFactionId);
    if (relation) relation.trust = Math.max(-100, Math.min(100, relation.trust + amount));
  }

  private adjustThreat(sourceFactionId: string, targetFactionId: string, amount: number) {
    const relation = this.relation(sourceFactionId, targetFactionId);
    if (relation) relation.threat = Math.max(0, Math.min(100, relation.threat + amount));
  }

  private relationKey(sourceFactionId: string, targetFactionId: string) { return `${sourceFactionId}:${targetFactionId}`; }
  private overlordOfWithoutEnsure(subjectFactionId: string) { return Object.values(GameState.instance.requireCampaign().subjectContracts).find((contract) => contract.subjectFactionId === subjectFactionId); }
  private interestModifier(interest: StrategicInterest) { return interest === 'ally' ? 14 : interest === 'friendly' ? 7 : interest === 'cautious' ? -4 : interest === 'rival' ? -12 : interest === 'hostile' ? -20 : 0; }
  private hash(value: string) { return [...value].reduce((sum, character, index) => ((sum * 33) + character.charCodeAt(0) + index) >>> 0, 5381); }
}
